import {
  PendingPosition,
  PositionOwner,
  samePositionOwner,
  isPendingSettled,
  pendingPositionKey,
} from '@/client/store/pending-position-store';
import { PositionStatus, UserPositionInfo } from '@/libs/sdk/api';

const toBigInt = (v: string | undefined) => {
  try {
    return BigInt(v || '0');
  } catch {
    return 0n;
  }
};

const toSyntheticRow = (position: PendingPosition): UserPositionInfo => {
  const shares = BigInt(position.shares);
  // Price scale matches backend rows: base-token wei per whole share, i.e.
  // amountIn/shares scaled by the option's own decimals.
  const entryPrice =
    shares > 0n ? ((BigInt(position.amountIn) * 10n ** BigInt(position.optionDecimal)) / shares).toString() : '0';
  return {
    balance: position.shares,
    baseTokenAddress: position.baseTokenAddress,
    betValue: position.amountIn,
    currentValue: position.amountIn,
    entryPrice,
    eventId: position.eventId,
    eventTitle: position.eventTitle,
    eventTitleI18n: position.eventTitleI18n,
    eventMarketCount: position.eventMarketCount,
    nameI18n: position.nameI18n,
    marketAddress: position.marketAddress,
    marketDescription: position.marketDescription,
    marketName: position.marketName,
    marketPicUrl: position.marketPicUrl,
    marketPirce: entryPrice,
    optionAddress: position.optionAddress,
    optionDecimal: position.optionDecimal,
    optionDescription: position.optionDescription,
    optionName: position.optionName,
    optionPicUrl: position.optionPicUrl,
    optionSymbol: position.optionName,
    pnl: '0',
    toWin: position.shares,
    deadline: position.deadline,
    status: PositionStatus.using,
  };
};

// Positive delta: bump the fetched row by the pending buy's shares and cost.
const applyBuyDelta = (row: UserPositionInfo, p: PendingPosition): UserPositionInfo => ({
  ...row,
  balance: (toBigInt(row.balance) + BigInt(p.shares)).toString(),
  betValue: (toBigInt(row.betValue) + BigInt(p.amountIn)).toString(),
  currentValue: (toBigInt(row.currentValue) + BigInt(p.amountIn)).toString(),
  toWin: (toBigInt(row.toWin) + BigInt(p.shares)).toString(),
});

// Negative delta: shrink the row's share-linear amounts proportionally; a
// delta that consumes the whole balance hides the row (fully sold).
const applySellDelta = (row: UserPositionInfo, p: PendingPosition): UserPositionInfo | undefined => {
  const balance = toBigInt(row.balance);
  const nextBalance = balance + BigInt(p.shares);
  if (balance <= 0n || nextBalance <= 0n) return undefined;
  const scale = (v: string | undefined) => ((toBigInt(v) * nextBalance) / balance).toString();
  return {
    ...row,
    balance: nextBalance.toString(),
    betValue: scale(row.betValue),
    currentValue: scale(row.currentValue),
    toWin: scale(row.toWin),
  };
};

// Overlay optimistic trades onto the fetched portfolio list: positive deltas
// bump the matching fetched row (or prepend a synthetic row when the backend
// has no row yet), negative deltas shrink or hide the fetched row. Settled
// entries are skipped here; removing them from the store is prunePending's
// job (effect in the list component).
export const mergePendingPositions = (
  list: UserPositionInfo[],
  pending: Record<string, PendingPosition>,
  owner: PositionOwner,
  dataUpdatedAt: number,
  now = Date.now()
): UserPositionInfo[] => {
  const active = Object.values(pending).filter(
    p => samePositionOwner(p.owner, owner) && !isPendingSettled(p, dataUpdatedAt, now) && BigInt(p.shares) !== 0n
  );
  if (active.length === 0) return list;
  const remaining = new Map(active.map(p => [pendingPositionKey(p.marketAddress, p.optionAddress, owner), p]));
  const merged = list
    .map(row => {
      const key = pendingPositionKey(row.marketAddress, row.optionAddress, owner);
      const p = remaining.get(key);
      if (!p) return row;
      remaining.delete(key);
      return BigInt(p.shares) > 0n ? applyBuyDelta(row, p) : applySellDelta(row, p);
    })
    .filter((row): row is UserPositionInfo => row !== undefined);
  // Unmatched negative deltas have nothing to shrink — never synthesize them.
  const synthetic = [...remaining.values()].filter(p => BigInt(p.shares) > 0n).map(toSyntheticRow);
  return [...synthetic, ...merged];
};
